// src/store/builders/usePortfolioStore.js
import { create } from "zustand";
import { toast } from "react-hot-toast";
import { axiosInstancev1 } from "../../lib/axiosInstance";
import useAuthStore from "../useAuthStore";

const usePortfolioStore = create((set, get) => ({

  isUploading: false,
  isUpdating: false,
  isDeleting: false,
  portfolio: [],

  // ---------------------------------------------
  // 👉 Upload Single Portfolio Image
  // ---------------------------------------------
  uploadPortfolioImage: async (file, description) => {
    try {
      set({ isUploading: true });

      const { token, user } = useAuthStore.getState();

      // Validate auth
      if (!token || !user || !user.id) {
        toast.error("Authentication missing!");
        set({ isUploading: false });
        return { success: false };
      }

      // Validate file input
      if (!file) {
        toast.error("No image file provided!");
        set({ isUploading: false });
        return { success: false };
      }

      // ---------------------------------------------
      // Build FormData (MUST MATCH BACKEND FIELD NAMES)
      // ---------------------------------------------
      const formData = new FormData();
      formData.append("portfolioImage", file);        // Backend expects THIS name
      formData.append("description", description || "");

      // ---------------------------------------------
      // API Request
      // ---------------------------------------------
      const response = await axiosInstancev1.post(
        `/builder/portfolio`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Backend returns 201 status with {success, message, portfolioId, imageUrl}
      if (response.status === 201 && response.data.success) {
        toast.success("Portfolio image uploaded successfully!");

        // Refresh portfolio to show new image
        await get().fetchPortfolio();

        set({ isUploading: false });
        return { success: true };
      }

      // Failed but backend responded without error
      set({ isUploading: false });
      return { success: false };

    } catch (error) {
      console.error("Upload Error:", error);

      toast.error(
        error?.response?.data?.message ||
        "Upload failed. Please try again."
      );

      set({ isUploading: false });
      return { success: false };
    }
  },

  // ---------------------------------------------
  // 👉 Fetch Portfolio Images (optional)
  // ---------------------------------------------
  fetchPortfolio: async () => {
    try {
      const { token, user } = useAuthStore.getState();

      if (!token || !user || !user.id) return;

      const response = await axiosInstancev1.get(
        `/user-info/builder/${user.id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success && response.data.builder) {
        const portArr = response.data.builder.portfolios || [];
        set({ portfolio: portArr });
      }
    } catch (error) {
      console.error("Fetch Portfolio Error:", error);
    }
  },

  // ---------------------------------------------
  // 👉 Update Portfolio Description
  // ---------------------------------------------
  updatePortfolio: async (portfolioId, description) => {
    try {
      set({ isUpdating: true });
      const { token } = useAuthStore.getState();

      if (!token) {
        toast.error("Not authenticated!");
        set({ isUpdating: false });
        return { success: false };
      }

      const response = await axiosInstancev1.put(
        `/builder/portfolio/${portfolioId}`,
        { description },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Backend returns 200 status on successful update
      if (response.status === 200) {
        toast.success("Portfolio updated successfully!");
        await get().fetchPortfolio();
        set({ isUpdating: false });
        return { success: true };
      }

      set({ isUpdating: false });
      return { success: false };

    } catch (error) {
      console.error("Update Error:", error);
      toast.error(
        error?.response?.data?.message || "Update failed."
      );
      set({ isUpdating: false });
      return { success: false };
    }
  },

  // ---------------------------------------------
  // 👉 Delete Portfolio Image
  // ---------------------------------------------
  deletePortfolio: async (portfolioId) => {
    try {
      set({ isDeleting: true });
      const { token } = useAuthStore.getState();

      if (!token) {
        toast.error("Not authenticated!");
        set({ isDeleting: false });
        return { success: false };
      }

      const response = await axiosInstancev1.delete(
        `/builder/portfolio/${portfolioId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Backend returns 200 status on successful deletion
      if (response.status === 200) {
        toast.success("Portfolio image deleted!");

        // Optimistically update UI immediately
        set((state) => ({
          portfolio: state.portfolio.filter((item) => item.id !== portfolioId)
        }));

        set({ isDeleting: false });
        return { success: true };
      }

      set({ isDeleting: false });
      return { success: false };

    } catch (error) {
      console.error("Delete Error:", error);
      toast.error(
        error?.response?.data?.message || "Delete failed."
      );
      set({ isDeleting: false });
      return { success: false };
    }
  },

  // ---------------------------------------------
  // 👉 Delete Multiple Portfolio Images
  // ---------------------------------------------
  deleteMultiplePortfolios: async (portfolioIds) => {
    try {
      if (!portfolioIds || portfolioIds.length === 0) return { success: false };

      set({ isDeleting: true });
      const { token } = useAuthStore.getState();

      if (!token) {
        toast.error("Not authenticated!");
        set({ isDeleting: false });
        return { success: false };
      }

      let successCount = 0;
      let failCount = 0;

      // Execute deletions (Promise.all for concurrency)
      const deletePromises = portfolioIds.map((id) =>
        axiosInstancev1.delete(`/builder/portfolio/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then(() => ({ id, success: true }))
          .catch(() => ({ id, success: false }))
      );

      const results = await Promise.all(deletePromises);

      const deletedIds = [];
      results.forEach((res) => {
        if (res.success) {
          successCount++;
          deletedIds.push(res.id);
        } else {
          failCount++;
        }
      });

      // Update state if any were deleted
      if (deletedIds.length > 0) {
        set((state) => ({
          portfolio: state.portfolio.filter((item) => !deletedIds.includes(item.id)),
        }));
      }

      // Show summary toast
      if (successCount > 0) {
        if (failCount === 0) {
          toast.success(`Successfully deleted ${successCount} images.`);
        } else {
          toast.success(`Deleted ${successCount} images. ${failCount} failed.`);
        }
      } else if (failCount > 0) {
        toast.error("Failed to delete selected images.");
      }

      set({ isDeleting: false });
      return { success: successCount > 0 };

    } catch (error) {
      console.error("Bulk Delete Error:", error);
      toast.error("An unexpected error occurred during bulk deletion.");
      set({ isDeleting: false });
      return { success: false };
    }
  },

}));

export default usePortfolioStore;
